import React, { useState, useEffect } from 'react';
import Switch from 'react-switch';
import { Employee } from '../../../../types';
import axiosInstance from '../../../../utils/axiosInstance';
import DeleteIcon from '../icons/delete';
import style from './EmployeeItem.module.css';
import { avatarColor, initialOf } from '../../../common/avatar';

interface EmployeeItemProps {
  /** §FB81: GUID власника компанії для PATCH обох перемикачів. */
  companyId: number | null;
  /** Майстер-перемикач компанії «Веб для відповідальних». Гейт для правого тумблера. */
  companyWebLiaisonEnabled: boolean;
  /** §FB170: зараз на чергуванні в кабінеті викликів (виклики з віджета йдуть туди). */
  onDuty?: boolean;
  employee: Employee;
  index: number;
  onDeleteClick: (employee: Employee) => void;
}

/**
 * §FB81-R23. Рядок співробітника з НЕЗАЛЕЖНИМИ перемикачами ролей.
 *
 * Це різні ролі, які раніше плуталися в одну:
 *
 *   1. «Дзвінки з QR» (`IsResponsible`) — кого зʼєднувати у тристоронньому дзвінку, коли
 *      нечуюча людина сканує QR-віджет компанії (глухий ініціатор + відповідальний + перекладач).
 *   2. «Вебкабінет» (`CompanyUser.WebLiaisonEnabled`) — хто входить у кабінет запланованих
 *      зустрічей (liaison) і планує тристоронні зустрічі з гостями.
 *   3. §FB172 «Адміністратор компанії» (`CompanyUser.IsCompanyAdmin`) — хто бачить у вебкабінеті
 *      все, що стосується компанії (лише перегляд). Вмикається на звернення компанії; зняття
 *      ролі одразу закриває відкритий кабінет людини. Керівник компанії має роль автоматично.
 *
 * Прапорці НЕ повʼязані й вільно поєднуються: людина може приймати дзвінки з віджета і не мати
 * кабінету, мати кабінет і не приймати дзвінки, мати обидва або жодного. Раніше кабінет вимагав
 * «галочки» відповідального, тож дати доступ лише до кабінету було неможливо.
 *
 * Правий перемикач активний ЛИШЕ коли увімкнено майстер-перемикач компанії — вимкнення того
 * майстра миттєво закриває вебдоступ усім, і показувати особисті тумблери «робочими» було б
 * оманою.
 *
 * Обидва перемикачі зберігаються МИТТЄВО, з відкатом стану при помилці — окремої кнопки
 * «Підтвердити» немає, щоб не було стану «перемкнув і забув зберегти».
 */
const EmployeeItem: React.FC<EmployeeItemProps> = ({
  companyId,
  companyWebLiaisonEnabled,
  onDuty = false,
  employee,
  index,
  onDeleteClick,
}) => {
  const [qrOn, setQrOn] = useState<boolean>(Boolean(employee.isResponsible));
  const [qrBusy, setQrBusy] = useState(false);
  const [webOn, setWebOn] = useState<boolean>(
    Boolean(employee.webLiaisonEnabled),
  );
  const [webBusy, setWebBusy] = useState(false);
  const [adminOn, setAdminOn] = useState<boolean>(
    Boolean(employee.isCompanyAdmin),
  );
  const [adminBusy, setAdminBusy] = useState(false);

  useEffect(() => {
    setQrOn(Boolean(employee.isResponsible));
  }, [employee.isResponsible]);

  useEffect(() => {
    setWebOn(Boolean(employee.webLiaisonEnabled));
  }, [employee.webLiaisonEnabled]);

  useEffect(() => {
    setAdminOn(Boolean(employee.isCompanyAdmin));
  }, [employee.isCompanyAdmin]);

  const handleQrToggle = async (checked: boolean) => {
    if (!companyId || qrBusy) return;
    setQrBusy(true);
    setQrOn(checked);
    try {
      await axiosInstance.patch('/Admin/SetResponsiblePerson', {
        id: employee.id,
        companyId,
        isResponsible: checked,
      });
    } catch (error) {
      setQrOn(!checked);
      console.error('Error updating responsible person switch:', error);
    } finally {
      setQrBusy(false);
    }
  };

  const handleWebToggle = async (checked: boolean) => {
    if (!companyId || webBusy || !companyWebLiaisonEnabled) return;
    setWebBusy(true);
    setWebOn(checked);
    try {
      await axiosInstance.patch('/LiaisonAdmin/SetCompanyUserWebLiaison', {
        userId: employee.id,
        companyId,
        enabled: checked,
      });
    } catch (error) {
      setWebOn(!checked);
      console.error('Error updating employee web liaison switch:', error);
    } finally {
      setWebBusy(false);
    }
  };

  const handleAdminToggle = async (checked: boolean) => {
    if (!companyId || adminBusy) return;
    setAdminBusy(true);
    setAdminOn(checked);
    try {
      await axiosInstance.patch('/LiaisonAdmin/SetCompanyUserCompanyAdmin', {
        userId: employee.id,
        companyId,
        enabled: checked,
      });
    } catch (error) {
      setAdminOn(!checked);
      console.error('Error updating company admin switch:', error);
    } finally {
      setAdminBusy(false);
    }
  };

  const switchColors = {
    onColor: '#0a8080',
    offColor: '#ccc',
    onHandleColor: '#f2f8f8',
    offHandleColor: '#fff',
    checkedIcon: false,
    uncheckedIcon: false,
    height: 16,
    width: 32,
  };

  return (
    <div className={style.itemContainer}>
      <div className={style.additionalElement}>{index + 1}</div>
      <div className={style.wrapInfo}>
        {employee.avatarUrl ? (
          <img src={employee.avatarUrl} alt={employee.userName} />
        ) : (
          <span
            className={style.avatarInitial}
            style={{ backgroundColor: avatarColor(employee.userName || '') }}
          >
            {initialOf(employee.userName || '')}
          </span>
        )}
        <p>{employee.userName}</p>
        {onDuty && (
          <span
            className={style.onDutyDot}
            title="На чергуванні в кабінеті викликів — виклики з віджета йдуть туди"
            aria-label="На чергуванні в кабінеті викликів"
            role="img"
          />
        )}
      </div>
      <p>{employee.email}</p>
      <p>{employee.phone}</p>

      <div className={style.togglesCell}>
        <span
          className={style.toggleBox}
          title="Приймає тристоронні дзвінки з QR-віджета компанії"
        >
          <Switch
            {...switchColors}
            onChange={handleQrToggle}
            checked={qrOn}
            disabled={qrBusy || !companyId}
          />
        </span>
        <span
          className={style.toggleBox}
          title={
            companyWebLiaisonEnabled
              ? 'Має доступ до вебкабінету запланованих зустрічей'
              : 'Спершу увімкніть «Веб для відповідальних» у даних компанії'
          }
        >
          <Switch
            {...switchColors}
            onChange={handleWebToggle}
            checked={companyWebLiaisonEnabled && webOn}
            disabled={webBusy || !companyId || !companyWebLiaisonEnabled}
          />
        </span>
        <span
          className={style.toggleBox}
          title="Бачить у вебкабінеті все, що стосується компанії (лише перегляд)"
        >
          <Switch
            {...switchColors}
            onChange={handleAdminToggle}
            checked={adminOn}
            disabled={adminBusy || !companyId}
          />
        </span>
      </div>

      <button
        onClick={() => onDeleteClick(employee)}
        className={style.deleteButton}
      >
        <DeleteIcon />
      </button>
    </div>
  );
};

export default EmployeeItem;
